import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InlineNotification, Modal, Tag } from '@carbon/react';
import { api } from '../api/client';
import { InlineError } from './states';
import { betDetailView, type BetDetail as Detail, type Section } from './betDetailView';

// The detail of one bet opened from the Lịch sử cược list (client request 2026-10-07): the round, the sides bet, the money and the
// balance before / after. Read-only; a phone gets the Carbon modal full width and the tables scroll inside it.
export type OpenBet = { nguon: string; ref: string; user_id: number };

function Block({ s }: { s: Section }) {
  return (
    <section className="bet-detail-block" data-section={s.id}>
      <h4>{s.title}</h4>
      {s.warn ? <InlineNotification kind="warning" lowContrast hideCloseButton title="" subtitle={s.note ?? ''} /> : null}
      {s.kv ? (
        <dl className="bet-kv">
          {s.kv.map(([k, v]) => (
            <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
          ))}
        </dl>
      ) : null}
      {s.table ? (
        <div className="bet-table-wrap">
          <table className="plain">
            <thead><tr>{s.table.head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>{s.table.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody>
          </table>
        </div>
      ) : null}
      {!s.warn && s.note ? <p className="muted bet-note">{s.note}</p> : null}
    </section>
  );
}

export function BetDetail({ bet, onClose }: { bet: OpenBet | null; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['bet-detail', bet?.nguon, bet?.ref, bet?.user_id],
    queryFn: () => api.get<Detail>('/api/admin/bets/detail', { nguon: bet!.nguon, ref: bet!.ref, userId: bet!.user_id }),
    enabled: !!bet,
    staleTime: 15_000,
  });
  // Escape closes wherever the focus is: Carbon's own handler did not close this modal on Escape (checked in Chromium), so it listens in the capture phase
  useEffect(() => {
    if (!bet) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc, true);
    return () => document.removeEventListener('keydown', esc, true);
  }, [bet, onClose]);
  const v = q.data ? betDetailView(q.data) : null;
  return (
    <Modal open={!!bet} passiveModal modalLabel="Chi tiết cược" modalHeading={v ? v.title : 'Đang tải...'} size="md" onRequestClose={onClose} className="bet-detail-modal">
      {q.error ? <InlineError error={q.error} /> : null}
      {v ? (
        <div className="stack">
          <div className="row">
            <Tag type={v.tag[1]} size="md">{v.tag[0]}</Tag>
            <span className="muted">{v.subtitle}</span>
          </div>
          {v.sections.map((s) => <Block key={s.id} s={s} />)}
        </div>
      ) : q.isLoading ? <p className="muted">Đang tải chi tiết cược...</p> : null}
    </Modal>
  );
}
